import { useMemo } from 'react';
import {
  IRoleMetaFieldsFragment,
  IUserGroupFieldsFragment,
  IUserPermissionFieldsFragment,
  RoleType,
} from '../../../utils/apollo/graphqlSchema';
import { assertNotNil } from '../../../utils/types';
import { appRoutes, PermissionUrlAction } from '../../../path';
import { capitalize } from 'lodash';
import { Link } from 'react-router-dom';
import { collectAllUniqRolesFromUser } from '../utils';
import useShowAdminSettings from '../../../utils/permissions/useShowAdminSettings';
import { useBootstrapSelector } from '../../../redux/selectorHooks';
import {
  defaultViewLabels,
  isDefaultViewMember,
} from '../../../utils/userSettings';
import { UnreachableCaseError } from 'ts-essentials';

// use actionName to match svgIcon name
type ActionName = 'pencil';

export interface IEntityContentItem {
  list: {
    title: string;
    items: React.ReactElement[];
  }[];
  isFlexRow: boolean;
  actionProps?: {
    name: ActionName;
    link: string;
    hint: string;
  };
}

interface IProps {
  entity:
    | IRoleMetaFieldsFragment
    | IUserPermissionFieldsFragment
    | IUserGroupFieldsFragment
    | null;
  noActions?: boolean;
}

/**
 * usePermissionEntityDescribeTable is a hook to generate content list for PermissionEntityDescribeTable component
 * This is a config file for different entity types and return a list of content for PermissionEntityDescribeTable
 * @param entity
 * @param noActions boolean
 */
const usePermissionEntityDescribeTable = ({ entity, noActions }: IProps) => {
  const showAdminSettings = useShowAdminSettings();
  const permissionConfig = useBootstrapSelector('permissionConfig');
  assertNotNil(permissionConfig);
  const { schemas } = permissionConfig;

  const contentList: IEntityContentItem[] = useMemo(() => {
    if (!entity) {
      return [];
    }
    if (entity.__typename === 'User') {
      // find all roles from user.groups and user.roles
      const uniqueRoles = collectAllUniqRolesFromUser(entity);
      return [
        {
          list: [
            {
              title: 'Assigned Group(s)',
              items: entity.groups.map((group) => {
                if (!showAdminSettings) {
                  return (
                    <span key={`assigned-groups-${group.id}`}>
                      {group.name}
                    </span>
                  );
                }

                return (
                  <Link
                    to={`${appRoutes.permission.userGroups}/${group.id}`}
                    key={`assigned-groups-${group.id}`}
                  >
                    {group.name}
                  </Link>
                );
              }),
            },
            {
              title: 'Assigned Role(s)',
              items: uniqueRoles.map((role) => {
                if (!showAdminSettings) {
                  return (
                    <span key={`assigned-roles-${role.id}`}>{role.name}</span>
                  );
                }

                return (
                  <Link
                    to={`${appRoutes.permission.roles}/${role.id}`}
                    key={`assigned-roles-${role.id}`}
                  >
                    {role.name}
                  </Link>
                );
              }),
            },
            {
              title: 'Default Schema',
              items: [
                <span key="default-schema">
                  {entity.defaultSchemaSetting.value
                    ? schemas.find(
                        (schema) =>
                          schema.id === entity.defaultSchemaSetting.value,
                      )?.name ?? ''
                    : ''}
                </span>,
              ],
            },
            {
              title: 'Default View',
              items: [
                <span key="default-view">
                  {entity.defaultViewSetting.value &&
                  isDefaultViewMember(entity.defaultViewSetting.value)
                    ? defaultViewLabels[entity.defaultViewSetting.value]
                    : ''}
                </span>,
              ],
            },
          ],
          actionProps: noActions
            ? undefined
            : {
                name: 'pencil',
                hint: 'Edit User Permissions',
                link:
                  appRoutes.permission.users +
                  '/' +
                  entity.id +
                  '/' +
                  PermissionUrlAction.Edit,
              },
          isFlexRow: true,
        },
      ];
    } else if (entity.__typename === 'Role') {
      return [
        {
          list: [
            {
              title: `${entity.name}${!entity.active ? ' [Disabled]' : ''}`,
              items: [
                <span key={`role-${entity.id}`}>{entity.description}</span>,
              ],
            },
          ],
          actionProps:
            entity.type !== RoleType.Admin
              ? {
                  name: 'pencil',
                  hint: 'Edit Role Permissions',
                  link:
                    appRoutes.permission.roles +
                    '/' +
                    entity.id +
                    '/' +
                    PermissionUrlAction.Edit,
                }
              : undefined,
          isFlexRow: false,
        },
      ];
    } else if (entity.__typename === 'UserGroup') {
      return [
        {
          list: [
            {
              title: `${entity.name}${!entity.active ? ' [Disabled]' : ''}`,
              items: [
                <span key={`user-group-${entity.id}`}>
                  {entity.description}
                </span>,
              ],
            },
          ],
          actionProps: {
            name: 'pencil',
            hint: 'Edit Group Permissions',
            link:
              appRoutes.permission.userGroups +
              '/' +
              entity.id +
              '/' +
              PermissionUrlAction.Edit,
          },
          isFlexRow: false,
        },
        {
          list: [
            {
              title: 'Assigned Role(s)',
              items: entity.roles.map((role) => (
                <Link
                  to={`${appRoutes.permission.roles}/${role.id}`}
                  key={`role-${role.id}`}
                >
                  {role.name}
                </Link>
              )),
            },
            {
              title: 'Group Type',
              items: [
                <span key={`group-${entity.id}`}>
                  {capitalize(entity.type)}
                </span>,
              ],
            },
          ],
          isFlexRow: true,
        },
      ];
    } else {
      throw new UnreachableCaseError(entity);
    }
  }, [entity, noActions, schemas, showAdminSettings]);
  return { contentList };
};

export default usePermissionEntityDescribeTable;
